/**
 * @fileoverview Hook Data Layer optimizado para CustomerDealItemMC. Obtiene status, currency, funnel, monto y fecha formateada con separación estricta de responsabilidades y performance avanzada.
 * @module useCustomerDealItemData
 * @category Hook - Data Layer
 * @since 1.0.0
 * @version 1.1.0 - JSDoc completo, performance y ejemplos
 *
 * @description
 * Hook especializado de **Data Layer** para CustomerDealItemMC. Implementa separación estricta de responsabilidades:
 * - ✅ Solo carga y formateo de datos de negocio (status, funnel, currency, monto, fecha)
 * - ❌ Sin lógica de UI, sin mutaciones, sin side effects
 * - ✅ Optimizaciones: useMemo para todos los cálculos, integración Redux selectiva
 *
 * **Flujo de operación:**
 * 1. Obtiene el status de la negociación según options
 * 2. Obtiene configuración regional (currency, símbolo)
 * 3. Selecciona el funnel desde Redux
 * 4. Formatea el monto y la fecha
 *
 * **Performance:**
 * - Todos los cálculos memoizados
 * - Solo recalcula si cambian dependencias críticas
 * - Sin re-fetch ni side effects
 *
 * @example
 * // Ejemplo de uso en componente
 * import { useCustomerDealItemData } from 'presentation/hooks/inbox/data/useCustomerDealItemData';
 *
 * const { status, currency, currency_symbol, selectedFunnel, formattedAmount, formattedDate } =
 *   useCustomerDealItemData({ deal, options, regionalConfig });
 *
 * @see CustomerDealItemMC Para integración visual
 */
import { useMemo } from "react";
import moment from "moment";
import { useSelector } from 'react-redux';
import { formatMoney } from '../../../../util/numberUtils';

/**
 * @typedef {Object} CustomerDealItemDataParams
 * @description Parámetros de entrada para el hook useCustomerDealItemData
 * @property {Object} deal - Objeto de negociación (deal) con propiedades relevantes: lead_status, funnel_id, amount, opening_date
 * @property {Array<{ value: string|number, label: string }>} options - Opciones de status disponibles para la negociación
 * @property {Object} regionalConfig - Configuración regional con currency y currency_symbol
 * @property {string} regionalConfig.currency - Código de moneda (ej: "USD")
 * @property {string} regionalConfig.currency_symbol - Símbolo de moneda (ej: "$", "€")
 */

/**
 * Hook Data Layer optimizado para CustomerDealItemMC.
 *
 * Implementa el patrón de **Data Layer** con separación estricta de responsabilidades:
 * - Solo obtiene y formatea datos de negocio para el componente visual.
 * - Sin lógica de UI ni mutaciones.
 *
 * @function useCustomerDealItemData
 * @category Hook - Data Layer
 *
 * @param {CustomerDealItemDataParams} params - Parámetros de entrada para el hook
 * @returns {CustomerDealItemDataResult} Objeto con status, currency, currency_symbol, selectedFunnel, formattedAmount, formattedDate
 *
 * @typedef {Object} CustomerDealItemDataResult
 * @property {Object} status - Status seleccionado de la negociación (opción encontrada en options)
 * @property {string} currency - Código de moneda (ej: "USD")
 * @property {string} currency_symbol - Símbolo de moneda (ej: "$", "€")
 * @property {Object} selectedFunnel - Funnel seleccionado con al menos { id }
 * @property {string} formattedAmount - Monto formateado con símbolo y moneda
 * @property {string} formattedDate - Fecha formateada DD-MM-YYYY
 *
 * @example
 * // Uso típico en componente
 * const { status, currency, currency_symbol, selectedFunnel, formattedAmount, formattedDate } =
 *   useCustomerDealItemData({ deal, options, regionalConfig });
 *
 * @see CustomerDealItemMC
 * @since 1.0.0
 * @version 1.1.0 - JSDoc completo, performance y ejemplos
 */
export function useCustomerDealItemData({ deal, options, regionalConfig }) {
  // Status de la negociación
  const status = useMemo(() => options.find(option => option.value === deal.lead_status), [options, deal.lead_status]);
  // Configuración regional
  const { currency, currency_symbol } = regionalConfig || {};
  // Funnels desde Redux
  const { funnels } = useSelector((reduxState) => reduxState.funnelsReducer);
  // Funnel seleccionado
  const selectedFunnel = useMemo(() => {
    const funnel = funnels?.find(f => String(f.web_id) === String(deal.funnel_id));
    return { id: funnel?.id };
  }, [deal.funnel_id, funnels]);
  // Monto formateado
  const formattedAmount = useMemo(() => {
    if (!deal.amount) return '';
    return `${currency_symbol || ''}${formatMoney(deal.amount)}${currency ? ' ' + currency : ''}`;
  }, [deal.amount, currency, currency_symbol]);
  // Fecha formateada
  const formattedDate = useMemo(() => {
    if (!deal.opening_date) return '';
    return moment(deal.opening_date).utc().format('DD-MM-YYYY');
  }, [deal.opening_date]);

  return { status, currency, currency_symbol, selectedFunnel, formattedAmount, formattedDate };
}
